import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Live product-page subtotal: unit price × quantity.
 * Updates on quantity selector changes and variant selection.
 *
 * @typedef {Object} ProductSubtotalRefs
 * @property {HTMLElement} [price]
 *
 * @extends {Component<ProductSubtotalRefs>}
 */
class ProductSubtotal extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    const detail = /** @type {{ cartLine?: number }} */ (event).detail;
    if (detail?.cartLine) return;

    this.update();
  };

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          this.dataset.productId = String(detail.newProduct.id);
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        const newSubtotal = detail.html.querySelector('[data-product-subtotal]');
        if (newSubtotal?.dataset.variantPrice) {
          this.dataset.variantPrice = newSubtotal.dataset.variantPrice;
        } else if (detail.resource?.price != null) {
          this.dataset.variantPrice = String(detail.resource.price);
        }

        this.update();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-subtotal] Event promise rejected:', error);
      });
  };

  /**
   * @returns {number}
   */
  #getQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('quantity-selector-component input[name="quantity"], input[name="quantity"]')
    );
    const value = parseInt(quantityInput?.value || '', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  update() {
    const unitPrice = parseInt(this.dataset.variantPrice || '0', 10) || 0;
    const quantity = this.#getQuantity();
    const total = unitPrice * quantity;
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';

    const priceEl = this.refs.price || this.querySelector('[data-product-subtotal-price]');
    if (!priceEl) return;

    priceEl.textContent = formatMoney(total, format, currency);
  }
}

if (!customElements.get('product-subtotal')) {
  customElements.define('product-subtotal', ProductSubtotal);
}
